import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";

interface DeviationPerformanceChartProps {
  month: number;
  year: number;
  ga_code: string;
  tourCardData?: any;
}

const DeviationPerformanceChart: React.FC<DeviationPerformanceChartProps> = ({
  month = new Date().getMonth() + 1,
  year = new Date().getFullYear(),
  ga_code,
  tourCardData,
}) => {
  const theme = useTheme();
   const minThreshold = tourCardData?.user?.min_deviation_threshold ?? 0;
  const maxThreshold = tourCardData?.user?.max_deviation_threshold ?? 100;

  // ✅ Extract and format last 3 months data from API
  const data = useMemo(() => {
    if (!tourCardData?.summary) return [];

    const summary = tourCardData.summary;

    const last3 = [];

    for (let i = 0; i < 3; i++) {
      const date = moment(`${year}-${month}`, "YYYY-MM").subtract(i, "months");

      const mm = date.format("MM");
      const yyyy = date.format("YYYY");

      // 🔎 find matching API month-year
      const match = summary.find(
        (item: any) => item.month === mm && item.year.toString() === yyyy
      );

      last3.push({
        name: date.format("MMM").toUpperCase(),
        value: match ? match.deviation_percentage : 0,
      });
    }

    return last3.reverse(); // latest month last
  }, [month, year, tourCardData]);


  // Safely read current & previous
  const current = data?.[2]?.value ?? 0;
  const previous = data?.[1]?.value ?? 0;
  const difference = Math.abs(current - previous).toFixed(2);

  // Determine trend ↑ ↓ →
  let arrow = "→";
  if (current > previous) arrow = "↑";
  else if (current < previous) arrow = "↓";

  // Determine control message
  let message = "Maintain Consistency";

  if (Number(difference) === 0) {
    message = "No Change, Stay Consistent";
  } else if (current < previous) {
    message = "Good Improvement!";
  } else if (current > previous) {
    message = "Deviation Rising, Take Action!";
  }

  // Apply threshold override
  if (current > maxThreshold) {
    message = "⚠ High Deviation – Immediate Focus Needed";
  } else if (current < minThreshold) {
    message = "✔ Excellent, Keep Going!";
  }


  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 0.1,
      }}
    >
      <CardContent sx={{ flex: 1, p: 1, "&:last-child": { pb: 2 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={500}>
            LAST QUARTER/MONTH
          </Typography>
        </Box>

        {/* Main content area */}
        <Box display="flex" alignItems="center" gap={2} height="calc(100% - 40px)">
          {/* Chart Section */}
          <Box sx={{ flex: 1, height: "100%", minHeight: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart 
                data={data} 
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorOrange" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FFA726" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#FFA726" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  interval={0}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: "rgba(255,255,255,0.95)",
                    border: "1px solid #ddd",
                    borderRadius: 4,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#FFA726"
                  strokeWidth={2.5}
                  fill="url(#colorOrange)"
                />
              </AreaChart>
            </ResponsiveContainer>
            
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                textAlign: "center",
              }}
            >
              Recommended: Below {minThreshold}%
            </Typography>
          </Box>

          {/* Right Section - Stats */}
          <Box 
            sx={{ 
              textAlign: "right",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minWidth: 120,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Vs Last Month
            </Typography>
            <Typography variant="h3" fontWeight={500} color="#FFA726" sx={{ lineHeight: 1.2 }}>
             {difference}%
            </Typography>
            <Typography
              variant="caption"
              sx={{ 
                color: "#E57373", 
                fontWeight: 600,
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.5,
              }}
            >
               {arrow} {message}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DeviationPerformanceChart;